import { useMemo, useState } from "react";

import { useApiList, useMutation } from "../api/hooks";
import { Badge, DataTable, Field, fmtDate, Modal, Money, Page, Pagination, Toolbar, useConfirm, useToast } from "../components/ui";
import type { EarningsAccount, Payout, Remittance } from "../api/types";

/** Cash collected by drivers that is owed back to the platform (02 §7). */
export function RemittancesPage() {
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const query = useMemo(() => ({ status: status || undefined, page, per_page: 25 }), [status, page]);
  const list = useApiList<Remittance & { id: string }>("/admin/remittances", query);
  const { run, busy } = useMutation();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  return (
    <Page title="Remittances">
      <Toolbar>
        <select className="input" value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))}>
          <option value="">Status: all</option>
          <option value="pending">Pending</option>
          <option value="collected">Collected</option>
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "order_code", label: "Order", render: (r) => <span className="mono">#{r.order_code}</span> },
          { key: "driver_name", label: "Driver" },
          { key: "amount", label: "Amount", align: "right", render: (r) => <Money value={r.amount} /> },
          { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
          { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "collected" ? "ok" : "warn"}>{r.status}</Badge> },
          { key: "collected_at", label: "Collected", render: (r) => fmtDate(r.collected_at) },
          {
            key: "_a",
            label: "",
            align: "right",
            render: (r) =>
              r.status === "pending" ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!(await confirm(`Mark ${r.amount} from ${r.driver_name} as collected?`))) return;
                    const res = await run(`/admin/remittances/${r.id}/collect`, { method: "POST" });
                    if (res.ok) {
                      toast("Collected");
                      list.reload();
                    } else toast(res.error, "err");
                  }}
                >
                  Mark collected
                </button>
              ) : null,
          },
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        empty="No remittances"
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
      {dialog}
    </Page>
  );
}

/** Ledger balances per vendor / driver, with a payout action. */
export function EarningsPage() {
  const [owner, setOwner] = useState<"vendors" | "drivers">("vendors");
  const [page, setPage] = useState(1);
  const list = useApiList<EarningsAccount & { id: string }>(`/admin/earnings/${owner}`, { page, per_page: 25 });
  const rows = list.data.map((a) => ({ ...a, id: a.account_id }));
  const [paying, setPaying] = useState<EarningsAccount | null>(null);

  return (
    <Page title="Earnings">
      <Toolbar>
        <select className="input" value={owner} onChange={(e) => (setOwner(e.target.value as "vendors" | "drivers"), setPage(1))}>
          <option value="vendors">Vendors</option>
          <option value="drivers">Drivers</option>
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "owner_name", label: owner === "vendors" ? "Vendor" : "Driver" },
          { key: "balance", label: "Balance", align: "right", render: (a) => <Money value={a.balance} /> },
          { key: "total_paid_out", label: "Paid out", align: "right", render: (a) => <Money value={a.total_paid_out} /> },
          { key: "currency", label: "Currency" },
          {
            key: "_a",
            label: "",
            align: "right",
            render: (a) => (
              <button type="button" className="btn btn-ghost btn-sm" disabled={Number(a.balance) <= 0} onClick={() => setPaying(a)}>
                Pay out
              </button>
            ),
          },
        ]}
        rows={rows}
        loading={list.loading}
        error={list.error}
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
      {paying && (
        <PayoutModal
          account={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            list.reload();
          }}
        />
      )}
    </Page>
  );
}

function PayoutModal({ account, onClose, onDone }: { account: EarningsAccount; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState(account.balance);
  const [note, setNote] = useState("");
  const { run, busy } = useMutation();
  const toast = useToast();
  return (
    <Modal title={`Pay out to ${account.owner_name}`} onClose={onClose}>
      <p className="muted">
        Balance <Money value={account.balance} />. The payout is recorded as a ledger debit; move the money through your bank separately.
      </p>
      <Field label="Amount">
        <input className="input" type="number" step="0.01" min="0.01" max={account.balance} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label="Note / reference">
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || Number(amount) <= 0}
          onClick={async () => {
            const r = await run("/admin/payouts", {
              method: "POST",
              body: { account_id: account.account_id, amount: Number(amount).toFixed(2), note: note || undefined },
            });
            if (r.ok) {
              toast("Payout recorded");
              onDone();
            } else toast(r.error, "err");
          }}
        >
          Record payout
        </button>
      </div>
    </Modal>
  );
}

export function PayoutsPage() {
  const [page, setPage] = useState(1);
  const list = useApiList<Payout & { id: string }>("/admin/payouts", { page, per_page: 25 });
  return (
    <Page title="Payouts">
      <DataTable
        columns={[
          { key: "at", label: "When", render: (p) => fmtDate(p.at) },
          { key: "owner_name", label: "Paid to" },
          { key: "amount", label: "Amount", align: "right", render: (p) => <Money value={p.amount} /> },
          { key: "note", label: "Note", render: (p) => p.note ?? "—" },
          { key: "created_by", label: "By", render: (p) => p.created_by ?? "—" },
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        empty="No payouts recorded yet"
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
    </Page>
  );
}
